import { emailRequestSchema, singleUseTokenSchema, type CurrentUser } from '@mfos/shared';
import { useMutation } from '@tanstack/react-query';
import { type SubmitEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErrorNotice, LoadingNotice, SuccessNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { PublicLayout } from '../../components/PublicLayout';
import { consumeMagicLink, requestMagicLink, useCompleteSignIn } from './auth.api';
import { MfaStep, type MfaChallenge } from './MfaStep';
import { useFragmentToken } from './use-fragment-token';
import { phrase } from '../../lib/i18n/runtime';

export function MagicLinkPage() {
  const token = useFragmentToken();
  return (
    <PublicLayout title={phrase('Sign in with an email link')}>
      {token === null ? <RequestLinkForm /> : <ConsumeLink token={token} />}
    </PublicLayout>
  );
}

function RequestLinkForm() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const mutation = useMutation({ mutationFn: requestMagicLink });

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const parsed = emailRequestSchema.safeParse({ email });
    if (!parsed.success) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(undefined);
    mutation.mutate(parsed.data);
  }

  if (mutation.isSuccess) {
    return (
      <>
        <SuccessNotice>
          {phrase(
            'If an account exists for that address, we sent a sign-in link. It expires soon and works once.',
          )}
        </SuccessNotice>
        <Link to="/login">{phrase('Back to sign in')}</Link>
      </>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="stack">
      <p>{phrase('We will email you a one-time link that signs you in without a password.')}</p>
      <TextField
        label={phrase('Email')}
        type="email"
        autoComplete="email"
        value={email}
        onValueChange={setEmail}
        error={emailError}
        required
      />
      <ErrorNotice error={mutation.error} />
      <button type="submit" className="button" disabled={mutation.isPending}>
        {mutation.isPending ? phrase('Sending…') : phrase('Email me a link')}
      </button>
      <Link to="/login">{phrase('Sign in with a password')}</Link>
    </form>
  );
}

function ConsumeLink({ token }: { token: string }) {
  const navigate = useNavigate();
  const completeSignIn = useCompleteSignIn();
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const started = useRef(false);
  const tokenIsWellFormed = singleUseTokenSchema.safeParse(token).success;
  const mutation = useMutation({ mutationFn: consumeMagicLink });
  const { mutate } = mutation;

  function onAuthenticated(user: CurrentUser) {
    completeSignIn(user);
    void navigate('/', { replace: true });
  }

  useEffect(() => {
    if (started.current || !tokenIsWellFormed) {
      return;
    }
    started.current = true;
    mutate(
      { token },
      {
        onSuccess: (result) => {
          if (result.status === 'AUTHENTICATED') {
            completeSignIn(result.user);
            void navigate('/', { replace: true });
          } else {
            setChallenge({ challengeToken: result.challengeToken, expiresAt: result.expiresAt });
          }
        },
      },
    );
  }, [token, tokenIsWellFormed, mutate, completeSignIn, navigate]);

  if (!tokenIsWellFormed) {
    return <InvalidLink />;
  }
  if (challenge !== null) {
    return (
      <MfaStep
        challenge={challenge}
        onAuthenticated={onAuthenticated}
        onCancel={() => void navigate('/login', { replace: true })}
      />
    );
  }
  if (mutation.isError) {
    return (
      <>
        <ErrorNotice error={mutation.error} />
        <InvalidLink />
      </>
    );
  }
  return <LoadingNotice>{phrase('Signing you in…')}</LoadingNotice>;
}

function InvalidLink() {
  return (
    <p>
      This sign-in link is invalid, expired or already used.{' '}
      <Link to="/magic-link">{phrase('Request a new link')}</Link> {phrase('or')}{' '}
      <Link to="/login">{phrase('sign in')}</Link>.
    </p>
  );
}
